import * as React from 'react';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';

import type {
    GetActivityForModal_activity as Activity,
    ActivityUpdateInput,
    GetPotentialActivityEntities_activityPotentialEntities_relatedEntities as RelatedEntity,
    GetTeams_teams as Team,
} from 'nutshell-graphql-types';

import type {Option} from '../../../core/select/graphql-select-inline';
import type {SelectOption} from '../../../setup/import/dromo/types';

import type {UpdateLocation} from '../../types';

import {
    ActivityTypeAndLeadsSection,
    TranscriptSummary,
    NoteSectionWrapper,
    FollowUpBanner,
} from '.';

import styles from './internal-notes.module.css';

type Props = {
    activity: Activity;
    handleUpdateActivity: (
        input: ActivityUpdateInput,
        location: UpdateLocation,
        shouldSetHasChanged?: boolean
    ) => Promise<any>;
    potentialRelatedEntities: RelatedEntity[] | null | undefined;
    handlePollForSummaryAndTranscript: () => Promise<any>;
    teams: Team[] | null | undefined;
};

export function InternalNotes(props: Props) {
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const [activityTypeSaveState, setActivityTypeSaveState] =
        React.useState<SavedSettingToastState>('initial');
    const [relatedLeadsSaveState, setRelatedLeadsSaveState] =
        React.useState<SavedSettingToastState>('initial');
    const [selectedRelatedEntities, setSelectedRelatedEntities] = React.useState<Option[]>(
        props.activity.relatedLeads && props.activity.relatedLeads.length > 0
            ? props.activity.relatedLeads.map((lead) => ({
                  id: lead.id,
                  name: lead.name,
                  avatarUrl: lead.avatarUrl,
                  initials: lead.initials,
                  metaData: {
                      href: lead.htmlUrl,
                  },
              }))
            : []
    );
    const [currentActivityType, setCurrentActivityType] = React.useState<
        SelectOption | null | undefined
    >(
        props.activity.activityType &&
            props.activity.activityType.id &&
            props.activity.activityType.name
            ? {
                  value: props.activity.activityType.id,
                  label: props.activity.activityType.name,
              }
            : null
    );

    const handleActivityTypeChange = (selectedOption: SelectOption) => {
        if (selectedOption === currentActivityType) {
            return;
        }

        setCurrentActivityType(selectedOption);
        setActivityTypeSaveState('saving');
        props
            .handleUpdateActivity(
                {
                    activity: props.activity.id,
                    activityType: selectedOption.value,
                },
                'internal-notes',
                false
            )
            .then(() => {
                setActivityTypeSaveState('complete');
            });
    };

    const handleRelatedLeadsChange = (selectedValues: Option[]) => {
        if (selectedValues === selectedRelatedEntities) {
            return;
        }

        setSelectedRelatedEntities(selectedValues);
        setRelatedLeadsSaveState('saving');
        props
            .handleUpdateActivity(
                {
                    activity: props.activity.id,
                    relatedLeadIds: selectedValues.map((value) => value.id),
                },
                'internal-notes'
            )
            .then(() => {
                setRelatedLeadsSaveState('complete');
            });
    };

    const hasLinkedActivity = Boolean(
        props.activity.previousActivity || props.activity.nextActivity
    );

    return (
        <div className='flex flex-dir-col full-width'>
            {hasLinkedActivity && (
                <FollowUpBanner
                    previousActivity={props.activity.previousActivity}
                    nextActivity={props.activity.nextActivity}
                />
            )}
            <div className={styles['internal-notes-container']}>
                <StackLegacy spacing={24}>
                    {errorMessage && (
                        <FlashBanner
                            type='error'
                            hasBorder={true}
                            noRightPadding={true}
                            duration={5000}
                        >
                            {errorMessage}
                        </FlashBanner>
                    )}
                    <ActivityTypeAndLeadsSection
                        currentActivityType={currentActivityType}
                        onActivityTypeChange={handleActivityTypeChange}
                        activityTypeSaveState={activityTypeSaveState}
                        setActivityTypeSaveState={setActivityTypeSaveState}
                        selectedRelatedEntities={selectedRelatedEntities}
                        onRelatedLeadsChange={handleRelatedLeadsChange}
                        potentialRelatedEntities={props.potentialRelatedEntities}
                        relatedLeadsSaveState={relatedLeadsSaveState}
                        setRelatedLeadsSaveState={setRelatedLeadsSaveState}
                    />
                    <NoteSectionWrapper
                        activityId={props.activity.id}
                        handleUpdateActivity={props.handleUpdateActivity}
                        quillDelta={props.activity.quillDelta}
                        teams={props.teams}
                    />
                    <TranscriptSummary
                        activityId={props.activity.id}
                        summary={props.activity.summary}
                        summaryStatus={props.activity.summary && props.activity.summary.status}
                        summaryState={
                            props.activity.summaryStates &&
                            props.activity.summaryStates.summaryEmptyState
                        }
                        summaryNextAction={
                            props.activity.summaryStates &&
                            props.activity.summaryStates.summaryNextAction
                        }
                        canViewSummaryDetails={props.activity.canViewSummaryDetails}
                        handlePollForSummaryAndTranscript={props.handlePollForSummaryAndTranscript}
                        setErrorMessage={setErrorMessage}
                        errorMessage={errorMessage}
                        generatingSummary={props.activity.generatingSummary}
                        transcriptionStatus={props.activity.transcriptionStatus}
                        isNotetakerDisabled={props.activity.summaryStates.isNotetakerDisabled}
                        tasks={(props.activity as any).tasks}
                    />
                </StackLegacy>
            </div>
        </div>
    );
}
